/**
 * Thread notification primitives (ticket 3531, upstream pin e3e7cc3f, MIT).
 * Ported from `apps/web/src/threadNotifications.ts`. The settings type comes
 * from the forge contracts instead of the t3code ones; everything else,
 * including the favicon badge canvas and the WebAudio unlock/buffer flow, is
 * upstream's. The desktop bridge badge calls are optional-chained and stay
 * no-ops until the desktop shell implements them.
 *
 * @module threadNotifications
 */
import type { NotificationMode } from "@cmmd-forge/contracts/settings";

import completionUrl from "./assets/notification-completion.mp3";
import inputUrl from "./assets/notification-input.mp3";

export const NOTIFICATION_MODE_LABELS = {
  off: "Off",
  notifications: "Notifications only",
  sound: "Sound only",
  "notifications-and-sound": "Notifications with sound",
} satisfies Record<NotificationMode, string>;

export function hasNotificationSound(mode: NotificationMode) {
  return mode === "sound" || mode === "notifications-and-sound";
}

export function hasDesktopNotifications(mode: NotificationMode) {
  return mode === "notifications" || mode === "notifications-and-sound";
}

let originalFavicon: HTMLLinkElement | undefined;
let badgeFavicon: HTMLLinkElement | undefined;

/**
 * The 64px red-dot PNG the browser tab shows over the favicon. Drawn only for
 * the web (no desktop shell) and win32, whose shell takes the image variant.
 */
function renderBadgeImage(count: number): string | null {
  if (count <= 0) return null;
  const bridge = window.desktopBridge;
  if (bridge && bridge.getClientPlatform?.() !== "win32") return null;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 64;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.fillStyle = "#e5484d";
  context.beginPath();
  context.arc(32, 32, 28, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = "white";
  context.font = `600 ${count > 9 ? 30 : 40}px "Segoe UI", sans-serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(count > 9 ? "9+" : String(count), 32, 34);
  return canvas.toDataURL("image/png");
}

/** Swap the tab favicon to the badge image, restoring the original at zero. */
function applyBadgeFavicon(image: string | null) {
  if (image) {
    if (!badgeFavicon) {
      originalFavicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]') ?? undefined;
      badgeFavicon = document.createElement("link");
      badgeFavicon.rel = "icon";
      badgeFavicon.type = "image/png";
      badgeFavicon.setAttribute("sizes", "64x64");
      originalFavicon?.remove();
      document.head.append(badgeFavicon);
    }
    badgeFavicon.href = image;
  } else if (badgeFavicon) {
    badgeFavicon.remove();
    badgeFavicon = undefined;
    if (originalFavicon) document.head.append(originalFavicon);
    originalFavicon = undefined;
  }
}

export function setNotificationBadge(count: number) {
  const image = renderBadgeImage(count);
  if (!window.desktopBridge) applyBadgeFavicon(image);
  void window.desktopBridge?.setNotificationBadge?.({ count, image }).catch(() => undefined);
}

let audioContext: AudioContext | undefined;
const buffers = new Map<string, Promise<AudioBuffer>>();

/** Called from a gesture so browsers allow later background playback. */
export function unlockNotificationAudio() {
  try {
    audioContext ??= new AudioContext();
  } catch {
    // No WebAudio in this context; playback stays silent.
    return;
  }
  void audioContext.resume().catch(() => undefined);
}

export async function playNotificationSound(
  kind: "completion" | "input",
  shouldPlay: () => boolean,
) {
  if (!audioContext || audioContext.state !== "running") return;
  const context = audioContext;
  const url = kind === "completion" ? completionUrl : inputUrl;
  try {
    let buffer = buffers.get(url);
    if (!buffer) {
      buffer = fetch(url)
        .then((response) => response.arrayBuffer())
        .then((data) => context.decodeAudioData(data));
      buffers.set(url, buffer);
    }
    const decoded = await buffer;
    if (!shouldPlay() || context.state !== "running") return;
    const source = context.createBufferSource();
    source.buffer = decoded;
    source.connect(context.destination);
    source.start();
  } catch {
    buffers.delete(url);
  }
}
